import React, { useEffect, useState, useRef } from "react";
import Axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import translations from "../assets/locals/translations";
import { Button } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { setShowMessage } from "../redux/slices/notificationSlice";

// --- CUSTOM COMPONENT: MultiSelect with Checkboxes & Save Button ---
const MultiSelectCheckboxes = ({ options, value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempSelected, setTempSelected] = useState([]);
  const wrapperRef = useRef(null);

  // Close dropdown if clicked outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef]);

  // When dropdown opens, sync internal state with Formik state
  useEffect(() => {
    if (isOpen) {
      setTempSelected(value || []);
    }
  }, [isOpen, value]);

  const handleCheckboxChange = (option) => {
    let newSelected = [...tempSelected];

    // Logic for "All" option
    if (option.label.toLowerCase() === "all") {
      const isAllSelected = newSelected.some((item) => item.label.toLowerCase() === "all");
      if (isAllSelected) {
        // Deselect All
        newSelected = [];
      } else {
        // Select All
        newSelected = [...options];
      }
    } else {
      // Logic for individual options
      const exists = newSelected.some((item) => item.value === option.value);

      if (exists) {
        // Remove item
        newSelected = newSelected.filter((item) => item.value !== option.value);
        // Also remove "All" tag if a single item is unchecked
        newSelected = newSelected.filter((item) => item.label.toLowerCase() !== "all");
      } else {
        // Add item
        newSelected.push(option);
      }
    }
    setTempSelected(newSelected);
  };

  const handleSave = () => {
    onChange(tempSelected); // Update Formik
    setIsOpen(false);       // Close Dropdown
  };

  const isChecked = (option) => {
    return tempSelected.some((item) => item.value === option.value);
  };

  return (
    <div className="position-relative" ref={wrapperRef}>
      {/* The Display Box (looks like a select input) */}
      <div
        className="form-control d-flex align-items-center justify-content-between"
        style={{ minHeight: "38px", cursor: "pointer", backgroundColor: "white" }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="text-truncate" style={{ maxWidth: "90%" }}>
          {value && value.length > 0 ? (
            value.map((v) => v.label).join(", ")
          ) : (
            <span className="text-muted">{placeholder}</span>
          )}
        </div>
        <i className={`fas fa-chevron-${isOpen ? "up" : "down"} text-secondary`} style={{ fontSize: "12px" }}></i>
      </div>

      {/* The Dropdown Menu */}
      {isOpen && (
        <div
          className="card position-absolute w-100 shadow"
          style={{ zIndex: 1050, marginTop: "5px", padding: "0" }}
        >
          <div className="p-2" style={{ maxHeight: "200px", overflowY: "auto" }}>
            {options.map((option, index) => (
              <div
                key={index}
                className="d-flex align-items-center p-2 border-bottom"
                style={{ cursor: "pointer" }}
                onClick={() => handleCheckboxChange(option)}
              >
                <input
                  type="checkbox"
                  checked={isChecked(option)}
                  onChange={() => { }} // Controlled by div click
                  style={{ marginRight: "10px", transform: "scale(1.2)", cursor: "pointer" }}
                />
                <span className="user-select-none">{option.label}</span>
              </div>
            ))}
          </div>

          {/* Footer with Save Button */}
          <div className="d-flex justify-content-end p-2 bg-light border-top">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary me-2"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={handleSave}
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
// --- END CUSTOM COMPONENT ---

const TaskForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const lastSegment = location.pathname.split("/").pop();
  const [filteredTasks, setFilteredTasks] = useState([]);
  const taskData = location.state?.data || {};
  const currentLanguage = useSelector((state) => state.language.language);
  const t = translations[currentLanguage] || translations['en'];

  // Holds lists for drop-down options
  const [newAddTask, setNewAddTask] = useState({
    employeeList: [],
    listDsp: [],
    listTasktype: [],
    listTask: [],
    listTasktimeperiod: [],
    listTaskStatus: [],
    listTaskCategory: [],
    listVillages: [],
    listTaluka: [],
    allocationTypes: [],
    outcomeOptions: [],
  });

  // For edit mode, we create a state for form initial values.
  const [initialFormValues, setInitialFormValues] = useState({
    employee: [],
    taskType: null,
    task: null,
    category: [],
    taskCount: "",
    tasktimePeriod: null,
    taskStatus: null,
    outcomeType: null,
    allocationType: null,
    taskArea: null,
    areaAllocatedPerson: null,
    startDate: "",
    endDate: "",
  });

  const validationSchema = Yup.object().shape({
    employee: Yup.array()
      .min(1, "Select at least one employee")
      .required("Required"),
    taskType: Yup.object().nullable().required("Task Type is required"),
    task: Yup.object().nullable().required("Task is required"),
    category: Yup.array()
      .min(1, "Select at least one category")
      .required("Required"),
    taskCount: Yup.number()
      .typeError("Must be a number")
      .positive("Must be positive")
      .required("Task count is required"),
    tasktimePeriod: Yup.object().nullable().required("Time period is required"),
    taskStatus: Yup.object().nullable().required("Task status is required"),
    outcomeType: Yup.object().nullable(),
    allocationType: Yup.object()
      .nullable()
      .required("Allocation type is required"),
    areaAllocatedPerson: Yup.array().nullable(),
    taskArea: Yup.array().nullable(),
    startDate: Yup.date().nullable().required("Start Date is required"),
    endDate: Yup.date()
      .nullable()
      .required("End Date is required")
      .min(Yup.ref("startDate"), "End date must be after start date"),
  });

  async function getTaskFormData() {
    setLoading(true); // Start loading
    const url = `${process.env.REACT_APP_NODE_URL}/api/get-task-formdata-list`;
    const config = {
      headers: {
        token: localStorage.getItem("rbacToken"),
      },
    };

    try {
      const response = await Axios.get(url, config);
      if (response.data && response.data.isSuccess) {
        const { optionLists } = response.data;
        setNewAddTask((prev) => ({
          ...prev,
          employeeList: optionLists.employeeList,
          listDsp: optionLists.dspList,
          listTasktype: optionLists.taskTypeList,
          listTask: optionLists.tasksList,
          listTasktimeperiod: optionLists.timePeriodsList,
          listTaskStatus: optionLists.taskStatusLst,
          listTaskCategory: optionLists.categoryList,
          listVillages: [
            { value: 0, label: 'All' },
            ...optionLists.villageList.filter((val) => val.value !== 1)
          ],
          listTaluka: [
            { value: 0, label: 'All' },
            ...optionLists.talukaList.filter((val) => val.value !== 1)
          ],
          allocationTypes: optionLists.allocationTypeList,
          outcomeOptions: optionLists.outcomeOptions,
        }));
      }
    } catch (error) {
      console.error("Error fetching task form data:", error);
    } finally {
      setLoading(false); // End loading
    }
  }

  // Load all drop-down lists on mount
  useEffect(() => {
    getTaskFormData();
  }, []);

  // ------------------- Edit Mode: Fetch existing task details -------------------
  useEffect(() => {
    // If the last URL segment is not "add", assume it is an ID for editing.
    if (taskData && taskData.id && newAddTask.listTask && lastSegment !== "add") {
      const config = {
        headers: {
          token: localStorage.getItem("rbacToken"),
        },
      };
      const url = `${process.env.REACT_APP_NODE_URL}/api/get-taskdata/${taskData.id}`;
      axios
        .get(url, config)
        .then((response) => {
          if (response.data?.isSuccess) {
            const taskData = response.data.formData;
            setInitialFormValues({
              employee: taskData.employee,
              taskType: taskData.taskType[0],
              task: taskData.task[0],
              category: taskData.category,
              taskCount: taskData.taskCount,
              tasktimePeriod: taskData.tasktimePeriod[0],
              taskStatus: taskData.taskStatus[0],
              outcomeType: taskData.outcomeType[0],
              allocationType: taskData.allocationType,
              // Depending on allocationType, you might have one or the other:
              taskArea: taskData.taskArea ? taskData.taskArea : null,
              areaAllocatedPerson: taskData.areaAllocatedPerson ? taskData.areaAllocatedPerson : null,
              startDate: taskData.startDate,
              endDate: taskData.endDate,
            });
            const filtered = newAddTask.listTask
              .filter((task) => task.id === taskData.task.value)
              .map((t) => ({
                value: t.id,
                label: t.task_name,
              }));
            setFilteredTasks(filtered);
          }
        })
        .catch((error) => {
          console.error("Error fetching task details:", error);
          dispatch(setShowMessage("Failed to load task details"));
        });
    }
  }, [taskData, lastSegment, newAddTask.listTask, dispatch]);

  // ------------------- Submit Handler -------------------
  const submitHandler = async (values) => {
    try {
      console.log("Submitting values:", values);
      let taskAreaData = [];

      if (values.taskArea?.length > 0) {
        taskAreaData.push({
          allocationType: values.allocationType.value,
          allottedIds: values.taskArea.map((a) => a.value),
        });
      }

      if (values.areaAllocatedPerson?.length > 0) {
        taskAreaData.push({
          allocationType: values.allocationType.value,
          allottedIds: values.areaAllocatedPerson.map((a) => a.value),
        });
      }

      values.taskAreaData = taskAreaData;

      const config = {
        headers: {
          token: localStorage.getItem("rbacToken"),
        },
      };

      let url = "";
      let response;
      if (lastSegment !== "add") {
        // Edit mode: update the task
        url = `${process.env.REACT_APP_NODE_URL}/api/update-task-data/${taskData.id}`;
        response = await axios.put(url, values, config);
      } else {
        // Add mode: create new task
        url = `${process.env.REACT_APP_NODE_URL}/api/addtask-data`;
        response = await axios.post(url, values, config);
      }

      if (response.data?.isSuccess) {
        console.log("Task processed successfully:", response.data);
        navigate("/administration/configuration/Task");
        dispatch(
          setShowMessage(
            lastSegment !== "add"
              ? "Task updated successfully."
              : "Task added successfully."
          )
        );
      } else {
        console.error("Failed to process task:", response.data);
        dispatch(setShowMessage("Failed to process task"));
      }
    } catch (error) {
      console.error("Error in submitting task:", error.message);
      dispatch(setShowMessage("Failed to process task"));
    }
  };

  return (
    <>
      <div className="addUser bg-white rounded p-3">
        <div className="row m-0">
          <div className="col-6">
            <h5 className="m-0">
              {lastSegment === "add" ? (t.addtask || "Add Task") : (t.edittask || "Edit Task")}
            </h5>
          </div>
          <div className="col-6 d-flex align-items-end justify-content-end">
            <Button
              variant="btn btn-warning mx-1"
              style={{
                width: "70px",
                height: "35px",
                fontSize: "14px",
                borderRadius: "20px",
              }}
              onClick={() => {
                navigate(-1);
              }}
            >
              {t.back || "Back"}
            </Button>
          </div>
        </div>
        <Formik
          initialValues={initialFormValues}
          enableReinitialize={true}
          validationSchema={validationSchema}
          onSubmit={submitHandler}
        >
          {({ values, handleSubmit, setFieldValue, errors, touched }) => {
            const onTaskTypeChange = async (selected) => {
              setFieldValue("taskType", selected);
              const filtered = newAddTask.listTask
                .filter((task) => task.tasktype_id === selected.value)
                .map((t) => ({
                  value: t.id,
                  label: t.task_name,
                }));
              setFilteredTasks(filtered);
              setFieldValue("task", null);
            };
            const onChangeAllocationType = (selected) => {
              setFieldValue("allocationType", selected);

              // Reset both when type changes
              setFieldValue("taskArea", []);
              setFieldValue("areaAllocatedPerson", []);
            };

            return (
              <Form className="row mt-3 m-0">
                {/* Employee Selection */}
                <section className="d-flex mt-3 flex-column col-12 col-sm-6 col-lg-4">
                  <label className="myLabel">
                    {t.employee || "Employee"}
                  </label>
                  <Select
                    isMulti
                    options={newAddTask.employeeList}
                    onChange={(selected) => setFieldValue("employee", selected)}
                    value={values.employee}
                  />
                  {touched.employee && errors.employee && (
                    <div className="text-danger">{errors.employee}</div>
                  )}
                </section>

                {/* Task Type Selection */}
                <section className="d-flex mt-3 flex-column col-12 col-sm-6 col-lg-4">
                  <label className="myLabel">
                    {t.tasktype || "Task Type"}
                  </label>
                  <Select
                    options={newAddTask.listTasktype}
                    onChange={onTaskTypeChange}
                    value={values.taskType}
                    placeholder={t.selectTaskType || "Select Task Type"}
                    isClearable
                  />
                  {touched.taskType && errors.taskType && (
                    <div className="text-danger">{errors.taskType}</div>
                  )}
                </section>

                {/* Task Selection */}
                <section className="d-flex mt-3 flex-column col-12 col-sm-6 col-lg-4">
                  <label className="myLabel">
                    {t.task || "Task"}
                  </label>
                  <Select
                    options={filteredTasks}
                    onChange={(selected) => setFieldValue("task", selected)}
                    value={values.task}
                    placeholder={t.selectTask || "Select Task"}
                    isClearable
                  />
                  {touched.task && errors.task && (
                    <div className="text-danger">{errors.task}</div>
                  )}
                </section>

                {/* Category Selection */}
                <section className="d-flex mt-3 flex-column col-12 col-sm-6 col-lg-4">
                  <label className="myLabel">
                    {t.category || "Category"}
                  </label>
                  <Select
                    isMulti
                    options={newAddTask.listTaskCategory}
                    onChange={(selected) => setFieldValue("category", selected)}
                    value={values.category}
                  />
                  {touched.category && errors.category && (
                    <div className="text-danger">{errors.category}</div>
                  )}
                </section>

                {/* Task Count */}
                <section className="d-flex mt-3 flex-column col-12 col-sm-6 col-lg-4">
                  <label className="myLabel">
                    {t.taskcount || "Task Count"}
                  </label>
                  <Field
                    className="myInput inputElement"
                    autoComplete="off"
                    type="text"
                    name="taskCount"
                  />
                  {touched.taskCount && errors.taskCount && (
                    <div className="text-danger">{errors.taskCount}</div>
                  )}
                </section>

                {/* Task Time Period Selection */}
                <section className="d-flex mt-3 flex-column col-12 col-sm-6 col-lg-4">
                  <label className="myLabel">
                    {t.tasktimep || "Task Time Period"}
                  </label>
                  <Select
                    options={newAddTask.listTasktimeperiod}
                    onChange={(selected) =>
                      setFieldValue("tasktimePeriod", selected)
                    }
                    value={values.tasktimePeriod}
                    placeholder={t.selectTaskTimePeriod || "Select Task Time Period"}
                  />
                  {touched.tasktimePeriod && errors.tasktimePeriod && (
                    <div className="text-danger">{errors.tasktimePeriod}</div>
                  )}
                </section>

                {/* Task Allocation Type Selection */}
                <section className="d-flex mt-3 flex-column col-12 col-sm-6 col-lg-4">
                  <label className="myLabel">{t.taskAllocationtype || "Task Allocation Type"}</label>
                  <Select
                    options={newAddTask.allocationTypes}
                    onChange={onChangeAllocationType}
                    value={values.allocationType}
                    placeholder={t.selectAllocationtype || "Select Allocation Type"}
                  />
                  {touched.allocationType && errors.allocationType && (
                    <div className="text-danger">{errors.allocationType}</div>
                  )}
                </section>

                {/* Task Area Selection (Village) - Still Standard Select */}
                {values.allocationType?.value === 2 && (
                  <section className="d-flex mt-3 flex-column col-12 col-sm-6 col-lg-4">
                    <label className="myLabel">{t.taskarea || "Village Area"}</label>
                    <Select
                      isMulti
                      options={newAddTask.listVillages}
                      onChange={(selected) => setFieldValue("taskArea", selected)}
                      value={values.taskArea}
                      placeholder="Select Village Area"
                    />
                  </section>
                )}

                {/* Task Area Selection (Taluka) - CUSTOM COMPONENT WITH SAVE BUTTON */}
                {values.allocationType?.value === 3 && (
                  <section className="d-flex mt-3 flex-column col-12 col-sm-6 col-lg-4">
                    <label className="myLabel">{t.taskarea || "Taluka Area"}</label>
                    <MultiSelectCheckboxes
                      options={newAddTask.listTaluka}
                      value={values.taskArea}
                      placeholder="Select Taluka Area"
                      onChange={(selected) => setFieldValue("taskArea", selected)}
                    />
                    {touched.taskArea && errors.taskArea && (
                      <div className="text-danger">{errors.taskArea}</div>
                    )}
                  </section>
                )}

                {/* Task Allocation Person List Selection */}
                {values.allocationType?.value === 1 && (
                  <section className="d-flex mt-3 flex-column col-12 col-sm-6 col-lg-4">
                    <label className="myLabel">{t.areaAllocatedPerson || "Area Allocated Person"}</label>
                    <Select
                      isMulti
                      options={newAddTask.listDsp}
                      onChange={(selected) =>
                        setFieldValue("areaAllocatedPerson", selected)
                      }
                      value={values.areaAllocatedPerson}
                      placeholder={t.selectAreaAllocatedPerson || "Select Area Allocated Person"}
                    />
                    {touched.areaAllocatedPerson &&
                      errors.areaAllocatedPerson && (
                        <div className="text-danger">
                          {errors.areaAllocatedPerson}
                        </div>
                      )}
                  </section>
                )}

                {/* Outcome type Selection */}
                <section className="d-flex mt-3 flex-column col-12 col-sm-6 col-lg-4">
                  <label className="myLabel">{t.outcomeType || "Outcome Type"}</label>
                  <Select
                    options={newAddTask.outcomeOptions}
                    getOptionLabel={(opt) => opt?.label ?? opt?.name ?? opt?.value ?? ""}
                    getOptionValue={(opt) => opt?.value ?? opt?.id ?? ""}
                    onChange={(selected) =>
                      setFieldValue("outcomeType", selected)
                    }
                    value={values.outcomeType}
                    placeholder={t.selectOutcometype || "Select Outcome Type"}
                  />
                  {touched.outcomeType && errors.outcomeType && (
                    <div className="text-danger">{errors.outcomeType}</div>
                  )}
                </section>

                {/* Task Status Selection */}
                <section className="d-flex mt-3 flex-column col-12 col-sm-6 col-lg-4">
                  <label className="myLabel">{t.taskStatus || "Task Status"}</label>
                  <Select
                    options={newAddTask.listTaskStatus}
                    onChange={(selected) =>
                      setFieldValue("taskStatus", selected)
                    }
                    value={values.taskStatus}
                    placeholder={t.selectTaskStatus || "Select Task Status"}
                  />
                  {touched.taskStatus && errors.taskStatus && (
                    <div className="text-danger">{errors.taskStatus}</div>
                  )}
                </section>

                {/* Start Date */}
                <section className="d-flex mt-3 flex-column col-12 col-sm-6 col-lg-4">
                  <label className="myLabel">{t.startDate || "Start Date"}</label>
                  <Field
                    type="date"
                    name="startDate"
                    className="form-control border border-secondary rounded"
                  />
                  {touched.startDate && errors.startDate && (
                    <div className="text-danger">{errors.startDate}</div>
                  )}
                </section>

                {/* End Date */}
                <section className="d-flex mt-3 flex-column col-12 col-sm-6 col-lg-4">
                  <label className="myLabel">{t.endDate || "End Date"}</label>
                  <Field
                    type="date"
                    name="endDate"
                    className="form-control border border-secondary rounded"
                  />
                  {touched.endDate && errors.endDate && (
                    <div className="text-danger">{errors.endDate}</div>
                  )}
                </section>

                {/* Submit Button */}
                <div className="d-flex gap-2 col-12 mt-4">
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="btn btn-primary"
                  >
                    {lastSegment !== "add" ? (t.updateTask || "Update Task") : (t.createTask || "Create Task")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      navigate(-1);
                    }}
                  >
                    {t.cancel || "Cancel"}
                  </button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </>
  );
};

export default TaskForm;